import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useAuth } from "../../../providers/AuthProvider";
import {
  listWorkspaces,
  type Workspace,
} from "../api/workspaces";
import { ApiError } from "../../../services/apiClient";
import { sortWorkspacesByName } from "../utils/sortWorkspaces";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SummaryCard, type SummaryMetric } from "../../../components/cards/SummaryCard";
import { InfoBanner } from "../../../components/InfoBanner";
import { SectionWithHeader } from "../../shared/ui/SectionWithHeader";
import { SkeletonList } from "../../shared/ui/skeletons";

import {
  ArrowLeft,
  ChartLineUp,
  GearSix,
  MapTrifold,
  Plus,
  Ruler,
  UsersThree,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";

type WorkspaceCardProps = {
  workspace: Workspace;
  formatNumber: (value: number, fractionDigits?: number) => string;
  canManage: boolean;
  roleBadge?: {
    label: string;
    slug: string;
  };
};

const actionsStackClasses = "flex flex-wrap items-center gap-3";
const secondaryButtonClasses = "pm-btn pm-btn-secondary";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const alertClasses =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const cardListClasses = "flex flex-col gap-4";
const badgeBaseClasses =
  "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide";
const roleBadgeClasses: Record<string, string> = {
  owner: "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/50 dark:bg-indigo-900/30 dark:text-indigo-300",
  editor: "border-green-200 bg-green-50 text-green-700 dark:border-green-900/50 dark:bg-green-900/30 dark:text-green-300",
  viewer: "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
};
const archivedBadgeClasses =
  "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/30 dark:text-rose-300";
const cardActionButtonClasses = "pm-btn pm-btn-secondary pm-btn-sm";
const cardIconButtonClasses = "pm-btn-icon";

export function MyWorkspacesPage() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [{ label: t("workspaces.my_workspaces.title") }];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    includeHome: true,
    fallback: { to: "/dashboard" },
  });

  const loadWorkspaces = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await listWorkspaces(accessToken);
      if (Array.isArray(result)) {
        setWorkspaces(result);
      } else if (
        result &&
        typeof result === "object" &&
        Array.isArray((result as { workspaces?: Workspace[] }).workspaces)
      ) {
        setWorkspaces((result as { workspaces: Workspace[] }).workspaces);
      } else {
        setWorkspaces([]);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.my_workspaces.load_error");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, t]);

  useEffect(() => {
    void loadWorkspaces();
  }, [loadWorkspaces]);

  const sortedWorkspaces = useMemo(() => {
    return sortWorkspacesByName(workspaces);
  }, [workspaces]);

  const resolveRole = useCallback(
    (workspace: Workspace): Workspace["role"] | undefined => {
      if (workspace.role) {
        return workspace.role;
      }
      if (user && workspace.owner_id === user.id) {
        return "owner";
      }
      return undefined;
    },
    [user],
  );

  const { owned: ownedWorkspaces, shared: sharedWorkspaces } = useMemo(() => {
    const owned: Workspace[] = [];
    const shared: Workspace[] = [];
    for (const workspace of sortedWorkspaces) {
      const role = resolveRole(workspace);
      if (role === "owner") {
        owned.push(workspace);
      } else {
        shared.push(workspace);
      }
    }
    return { owned, shared };
  }, [sortedWorkspaces, resolveRole]);

  const canManageWorkspace = useCallback(
    (workspace: Workspace) => resolveRole(workspace) === "owner",
    [resolveRole],
  );

  const formatRoleLabel = useCallback(
    (workspace: Workspace) => {
      const role = resolveRole(workspace);
      if (!role) {
        return null;
      }
      return role.charAt(0).toUpperCase() + role.slice(1);
    },
    [resolveRole],
  );

  const formatNumber = useCallback(
    (value: number, fractionDigits = 0) =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(value ?? 0),
    [],
  );

  if (!user || !accessToken) {
    return null;
  }

  return (
    <PageShell width="wide">
        <Breadcrumbs items={breadcrumbItems} />

        <PageHeader
          title={t("workspaces.my_workspaces.title")}
          subtitle={t("workspaces.my_workspaces.subtitle")}
          actions={
            <div className={actionsStackClasses}>
              <Link to="/workspaces/map" className={secondaryButtonClasses}>
                <MapTrifold {...defaultIconProps} aria-hidden />
                {t("workspaces.my_workspaces.all_lots_map_button")}
              </Link>
              <Link to="/workspaces/new" className={primaryButtonClasses}>
                <Plus {...defaultIconProps} aria-hidden />
                {t("workspaces.my_workspaces.create_button")}
              </Link>
              <button
                type="button"
                className={ghostButtonClasses}
                onClick={handleBack}
              >
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("actions.back")}
              </button>
            </div>
          }
        />

        <InfoBanner
          title={t("workspaces.my_workspaces.banner.title")}
          description={t("workspaces.my_workspaces.banner.description")}
          tone="info"
          icon={<UsersThree {...defaultIconProps} aria-hidden />}
          dismissId="workspace-workspaces-banner"
          dismissDurationS={60 * 60 * 24 * 30}
        />

        {error && (
          <div className={alertClasses} role="alert">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-10">
          {isLoading ? (
            <SkeletonList count={3} />
          ) : (
            <>
              {ownedWorkspaces.length === 0 &&
              sharedWorkspaces.length === 0 ? (
                <InfoBanner
                  icon={<SquaresFourIcon {...defaultIconProps} aria-hidden />}
                  title={t("workspaces.my_workspaces.empty_state.title")}
                  description={t(
                    "workspaces.my_workspaces.empty_state.description",
                  )}
                >
                  <Link to="/workspaces/new" className={primaryButtonClasses}>
                    <Plus {...defaultIconProps} aria-hidden />
                    {t("workspaces.my_workspaces.create_button")}
                  </Link>
                </InfoBanner>
              ) : null}

              {ownedWorkspaces.length > 0 ? (
                <SectionWithHeader
                  aria-label={t("workspaces.my_workspaces.section_owned_aria")}
                  title={t("workspaces.my_workspaces.owned_by_you")}
                  description={t(
                    "workspaces.my_workspaces.owned_description",
                  )}
                >
                  <div className={cardListClasses}>
                    {ownedWorkspaces.map((workspace) => (
                      <WorkspaceCard
                        key={workspace.id}
                        workspace={workspace}
                        formatNumber={formatNumber}
                        canManage
                      />
                    ))}
                  </div>
                </SectionWithHeader>
              ) : null}

              {sharedWorkspaces.length > 0 ? (
                <SectionWithHeader
                  aria-label={t("workspaces.my_workspaces.section_shared_aria")}
                  title={t("workspaces.my_workspaces.shared_with_you")}
                  description={t(
                    "workspaces.my_workspaces.shared_description",
                  )}
                >
                  <div className={cardListClasses}>
                    {sharedWorkspaces.map((workspace) => {
                      const roleSlug = (workspace.role ?? "viewer").toLowerCase();
                      const roleLabel = formatRoleLabel(workspace) ?? "Viewer";
                      const canManage = canManageWorkspace(workspace);
                      return (
                        <WorkspaceCard
                          key={workspace.id}
                          workspace={workspace}
                          formatNumber={formatNumber}
                          canManage={canManage}
                          roleBadge={{ label: roleLabel, slug: roleSlug }}
                        />
                      );
                    })}
                  </div>
                </SectionWithHeader>
              ) : null}
            </>
          )}
        </div>
    </PageShell>
  );
}

function WorkspaceCard({
  workspace,
  formatNumber,
  canManage,
  roleBadge,
}: WorkspaceCardProps) {
  const { t } = useTranslation();
  const workspaceState = { workspace };
  const isArchived = Boolean(workspace.archived_at);
  const roleTone =
    (roleBadge?.slug && roleBadgeClasses[roleBadge.slug]) ||
    roleBadgeClasses.viewer;
  const badges = (
    <>
      {roleBadge ? (
        <span className={`${badgeBaseClasses} ${roleTone}`}>
          {roleBadge.label}
        </span>
      ) : null}
      {isArchived ? (
        <span className={`${badgeBaseClasses} ${archivedBadgeClasses}`}>
          {t("workspaces.my_workspaces.archived_badge")}
        </span>
      ) : null}
    </>
  );

  const metrics: SummaryMetric[] = [
    {
      icon: <SquaresFourIcon {...defaultIconProps} />,
      label: t("workspaces.my_workspaces.active_lots"),
      value: formatNumber(workspace.agg_active_lots),
    },
    {
      icon: <ChartLineUp {...defaultIconProps} />,
      label: t("workspaces.my_workspaces.total_area"),
      value: t("common.units.square_meters", {
        value: formatNumber(workspace.agg_computed_area_m2, 2),
      }),
    },
    {
      icon: <Ruler {...defaultIconProps} />,
      label: t("workspaces.my_workspaces.traverse_length"),
      value: t("common.units.meters", {
        value: formatNumber(workspace.agg_traverse_length_m, 2),
      }),
    },
  ];

  const actions = (
    <>
      <Link
        to={`/workspaces/${workspace.id}/lots`}
        state={workspaceState}
        className={cardActionButtonClasses}
      >
        <SquaresFourIcon {...defaultIconProps} aria-hidden />
        {t("workspaces.my_workspaces.lots_button")}
      </Link>
      <Link
        to={`/workspaces/${workspace.id}/lots/map`}
        state={workspaceState}
        className={cardActionButtonClasses}
      >
        <MapTrifold {...defaultIconProps} aria-hidden />
        {t("workspaces.my_workspaces.map_button")}
      </Link>
      {canManage ? (
        <Link
          to={`/workspaces/${workspace.id}/members`}
          state={workspaceState}
          className={cardActionButtonClasses}
        >
          <UsersThree {...defaultIconProps} aria-hidden />
          {t("workspaces.my_workspaces.members_button")}
        </Link>
      ) : null}
      {canManage ? (
        <Link
          to={`/workspaces/${workspace.id}/edit`}
          state={workspaceState}
          className={cardIconButtonClasses}
          aria-label={t("workspaces.my_workspaces.edit_label", {
            name: workspace.name,
          })}
        >
          <GearSix {...defaultIconProps} aria-hidden />
        </Link>
      ) : null}
    </>
  );

  const cardClasses = isArchived
    ? "border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/30"
    : undefined;

  return (
    <SummaryCard
      tone="interactive"
      className={cardClasses}
      link={{
        to: `/workspaces/${workspace.id}/lots`,
        state: workspaceState,
        label: t("workspaces.my_workspaces.open_label", {
          name: workspace.name,
        }),
      }}
      title={<span className="block truncate">{workspace.name}</span>}
      subtitle={workspace.description || undefined}
      badges={badges}
      actions={actions}
      metrics={metrics}
    />
  );
}
